import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../../../contexts/LanguageContext";

interface CalculatorFormProps {
  formData: any;
  countries: any[];
  currencies: any[];
  categories: any[];
  shippingCompanies: any[];
  cities: any[];
  paymentMethods: any[];
  weightMeasurementUnits: any[];
  lengthMeasurementUnits: any[];
  result: any;
  isLBPriceEnabled: boolean;
  setIsLBPriceEnabled: (enabled: boolean) => void;
  handleInputChange: (event: any) => void;
  handleQuantityChange: (increment: number) => void;
  handleConvertMerchantDeliveryFee?: (baseCurrencyAmount: number) => void;
  calculateOrder: () => void;
  resetForm: () => void;
  calculating: boolean;
  resetting: boolean;
  type: 'order' | 'shipforme';
}

export const CalculatorForm: React.FC<CalculatorFormProps> = ({
  formData,
  countries,
  currencies,
  categories,
  shippingCompanies,
  cities,
  paymentMethods,
  weightMeasurementUnits,
  lengthMeasurementUnits,
  result,
  isLBPriceEnabled,
  setIsLBPriceEnabled,
  handleInputChange,
  handleQuantityChange,
  handleConvertMerchantDeliveryFee,
  calculateOrder,
  resetForm,
  calculating,
  resetting,
  type,
}) => {
  const { t } = useTranslation(['orders', 'common']);
  const { isRTL, language } = useLanguage();
  const iqdLabel = language === 'en' ? 'IQD' : 'د.ع';

  const formatAmount = (value?: number) => {
    const safeValue = typeof value === 'number' && !Number.isNaN(value) ? value : 0;
    return safeValue.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Get the selected shipping company
  const selectedShippingCompany = shippingCompanies.find(c => c.id === formData.shippingCompanyId);

  // Validation function
  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'originCountryId':
        return !value || value === '' || value === '0' ? t('orders:validation.originCountryRequired') : '';
      case 'destinationCountryId':
        return !value || value === '' || value === '0' ? t('orders:validation.destinationCountryRequired') : '';
      case 'cityId':
        return !value || value === '' || value === '0' ? t('orders:validation.cityRequired') : '';
      case 'paymentMethodId':
        return type === 'order' && (!value || value === '' || value === '0') ? t('orders:validation.paymentMethodRequired') : '';
      case 'paymentType':
        return value === '' || value === undefined || value === null ? t('orders:validation.paymentTypeRequired') : '';
      case 'categoryId':
        return !value || value === '' || value === '0' ? t('orders:validation.categoryRequired') : '';
      case 'itemPrice':
        return value === '' || value === null || value === undefined || Number(value) < 0 ? t('orders:validation.itemPriceRequired') : '';
      case 'merchentDeliveryFee':
        return type === 'order' && (value === '' || value === null || value === undefined || Number(value) < 0) ? t('orders:validation.merchantDeliveryFeeRequired') : '';
      case 'currencyId':
        return !value || value === '' || value === '0' ? t('orders:validation.currencyRequired') : '';
      case 'width':
        return value === '' || value === null || value === undefined || Number(value) < 0 ? t('orders:validation.widthRequired') : '';
      case 'height':
        return value === '' || value === null || value === undefined || Number(value) < 0 ? t('orders:validation.heightRequired') : '';
      case 'length':
        return value === '' || value === null || value === undefined || Number(value) < 0 ? t('orders:validation.lengthRequired') : '';
      case 'weight':
        return value === '' || value === null || value === undefined || Number(value) < 0 ? t('orders:validation.weightRequired') : '';
      case 'shippingCompanyId':
        return !value || value === '' || value === '0' ? t('orders:validation.shippingCompanyRequired') : '';
      default:
        return '';
    }
  };

  // Validate all fields
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const fieldsToValidate = [
      'originCountryId',
      'destinationCountryId',
      'cityId',
      'paymentType',
      'categoryId',
      'itemPrice',
      'currencyId',
      'width',
      'height',
      'length',
      'weight',
      'shippingCompanyId'
    ];

    // Add order-specific fields
    if (type === 'order') {
      fieldsToValidate.push('paymentMethodId', 'merchentDeliveryFee');
    }

    fieldsToValidate.forEach(field => {
      const fieldValue = formData[field];
      const error = validateField(field, fieldValue);
      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field blur
  const handleFieldBlur = (name: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name]);
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  // Enhanced input change handler
  const handleInputChangeWithValidation = (event: any) => {
    const { name } = event.target;

    handleInputChange(event);

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Enhanced calculate order function
  const handleCalculateOrder = () => {
    if (validateForm()) {
      calculateOrder();
    } else {
      // Mark all fields as touched to show individual field errors
      const fieldsToValidate = [
        'originCountryId',
        'destinationCountryId',
        'cityId',
        'paymentType',
        'categoryId',
        'itemPrice',
        'currencyId',
        'width',
        'height',
        'length',
        'weight',
        'shippingCompanyId'
      ];

      if (type === 'order') {
        fieldsToValidate.push('paymentMethodId', 'merchentDeliveryFee');
      }

      const newTouched: Record<string, boolean> = {};
      fieldsToValidate.forEach(field => {
        newTouched[field] = true;
      });
      setTouched(newTouched);
    }
  };

  // Get field class name with error styling
  const getFieldClassName = (fieldName: string): string => {
    const baseClass = 'form-control';
    const hasError = touched[fieldName] && errors[fieldName];
    return hasError ? `${baseClass} is-invalid` : baseClass;
  };

  // Render error message
  const renderErrorMessage = (fieldName: string) => {
    if (touched[fieldName] && errors[fieldName]) {
      return <div className="invalid-feedback d-block">{errors[fieldName]}</div>;
    }
    return null;
  };

  return (
    <div>
      {/* <div className="row">
        <div className="col-md-6">
          <label className="form-label">{t('fields.link')}</label>
          <input
            type="text"
            className="form-control"
            name="link"
            value={formData.link || ""}
            onChange={handleInputChange}
            placeholder={t('placeholders.enterLink')}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t('fields.merchantName')}</label>
          <input
            type="text"
            className="form-control"
            name="merchantName"
            value={formData.merchantName || ""}
            onChange={handleInputChange}
            placeholder={t('placeholders.enterMerchantName')}
          />
        </div>
      </div> */}

      {/* <div className="row mt-3">
        <div className="col-md-6">
          <label className="form-label">{t('fields.itemOptions')}</label>
          <input
            type="text"
            className="form-control"
            name="description"
            placeholder={t('placeholders.enterDescription')}
            value={formData.description || ""}
            onChange={handleInputChange}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">{t('fields.extendedDescription')}</label>
          <textarea
            className="form-control"
            name="extendedDescription"
            value={formData.extendedDescription || ""}
            onChange={handleInputChange}
            placeholder={t('placeholders.enterExtendedDescription')}
          />
        </div>
      </div> */}

      <div className="row mt-3">
        <div className="col-md-6">
          <label className="form-label">
            {t('fields.originCountry')} <span className="text-danger">*</span>
          </label>
          <select
            key={formData.originCountryId}
            className={getFieldClassName('originCountryId')}
            name="originCountryId"
            value={formData.originCountryId}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('originCountryId')}
            required
          >
            <option value="">{t('placeholders.selectOriginCountry')}</option>
            {countries
              .filter((x: any) => x.countryType == 2 || x.countryType == 0)
              .map((country: any) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
          </select>
          {renderErrorMessage('originCountryId')}
        </div>
        <div className="col-md-6">
          <label className="form-label">
            {t('fields.destinationCountry')} <span className="text-danger">*</span>
          </label>
          <select
            key={formData.destinationCountryId}
            className={getFieldClassName('destinationCountryId')}
            name="destinationCountryId"
            value={formData.destinationCountryId}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('destinationCountryId')}
            required
          >
            <option value="">{t('placeholders.selectDestinationCountry')}</option>
            {countries
              .filter((x: any) => x.countryType == 1 || x.countryType == 0)
              .map((country: any) => (
                <option key={country.id} value={country.id}>
                  {country.name}
                </option>
              ))}
          </select>
          {renderErrorMessage('destinationCountryId')}
        </div>

        <div className="col-md-6">
          <label className="form-label">
            {t('fields.city')} <span className="text-danger">*</span>
          </label>
          <select
            key={formData.cityId}
            className={getFieldClassName('cityId')}
            name="cityId"
            value={formData.cityId || ""}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('cityId')}
            required
          >
            <option value="">{t('placeholders.selectCity')}</option>
            {cities
              .filter((city: any) => city.countryId == formData.destinationCountryId)
              .map((city: any) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
          </select>
          {renderErrorMessage('cityId')}
        </div>

        {type === 'order' && (

          <>
            <div className="col-md-6">
              <label className="form-label">
                {t('fields.purchaseMethod', 'Purchase Method')} <span className="text-danger">*</span>
              </label>
              <select
                key={formData.paymentMethodId}
                className={getFieldClassName('paymentMethodId')}
                name="paymentMethodId"
                value={formData.paymentMethodId}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('paymentMethodId')}
                required
              >
                <option value="">{t('placeholders.selectPurchaseMethod', 'Select Purchase Method')}</option>
                {paymentMethods.map((paymentMethod: any) => (
                  <option key={paymentMethod.id} value={paymentMethod.id}>
                    {paymentMethod.name + "(" + paymentMethod.percent + ")"}
                  </option>
                ))}
              </select>
              {renderErrorMessage('paymentMethodId')}
            </div></>
        )}

        {/* <div className="col-md-6">
          <label className="form-label">
            {t('fields.paymentType')} <span className="text-danger">*</span>
          </label>
          <select
            className={getFieldClassName('paymentType')}
            name="paymentType"
            value={formData.paymentType}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('paymentType')}
            required
          >
            <option value="">{t('placeholders.selectPaymentType')}</option>
            <option value={0} >{t('paymentTypes.0')}</option>
            <option value={1}>{t('paymentTypes.1')}</option>
            <option value={2} selected>{t('paymentTypes.2')}</option>
          </select>
          {renderErrorMessage('paymentType')}
        </div> */}

        {/* Initial Partial Payment Field - Only show when Partial Payment is selected */}
        {formData.paymentType == 1 && (
          <div className="col-md-6">
            <label className="form-label">
              {t('fields.initialPartialPayment')} <span className="text-danger">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              className={getFieldClassName('initialPartialPayment')}
              name="initialPartialPayment"
              value={formData.initialPartialPayment || ''}
              onChange={handleInputChange}
              onBlur={() => handleFieldBlur('initialPartialPayment')}
              placeholder={t('placeholders.enterInitialPartialPayment')}
            />
            {renderErrorMessage('initialPartialPayment')}
          </div>
        )}
        {/* </div>

      <div className="row mt-3"> */}
        <div className="col-md-6">
          <label className="form-label">
            {t('fields.category')} <span className="text-danger">*</span>
          </label>
          <select
            key={formData.categoryId}
            className={getFieldClassName('categoryId')}
            name="categoryId"
            value={formData.categoryId}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('categoryId')}
            required
          >
            <option value="">{t('placeholders.selectCategory')}</option>
            {categories.map((category: any) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {renderErrorMessage('categoryId')}
        </div>
      </div>


      <div className="row mt-3">
        <div className="col-md-3">
          <label className="form-label">{t('fields.quantity')}</label>
          <div className={`d-flex ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              className={`btn btn-outline-primary btn-sm rounded-circle ${isRTL ? 'ms-1' : 'me-1'}`}
              onClick={() => handleQuantityChange(-1)}
            >
              −
            </button>
            <span className="px-2">{formData.quantity}</span>
            <button
              className={`btn btn-outline-primary btn-sm rounded-circle ${isRTL ? 'me-1' : 'ms-1'}`}
              onClick={() => handleQuantityChange(1)}
            >
              +
            </button>
          </div>
        </div>
        <div className="col-md-3">
          <label className="form-label">
            {t('fields.currency')} <span className="text-danger">*</span>
          </label>
          <select
            className={getFieldClassName('currencyId')}
            name="currencyId"
            value={formData.currencyId || ""}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('currencyId')}
            required
          >
            <option value="">{t('placeholders.selectCurrency')}</option>
            {currencies.map((item: any) => (
              <option key={item.id} value={item.id}>
                {item.name + "(" + item.lastExchangeRate + ")"}
              </option>
            ))}
          </select>
          {renderErrorMessage('currencyId')}
        </div>
        <div className="col-md-3">
          <label className="form-label">
            {t('fields.itemPrice')} <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={getFieldClassName('itemPrice')}
            name="itemPrice"
            value={formData.itemPrice}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('itemPrice')}
            placeholder={t('placeholders.enterItemPrice')}
            required
          />
          {renderErrorMessage('itemPrice')}
        </div>
        {type === 'order' && (
          <>
            <div className="col-md-3">
              <label className="form-label">
                {t('fields.merchantDeliveryFee')} <span className="text-danger">*</span>
              </label>
              <input
                type="number"
                className={getFieldClassName('merchentDeliveryFeeInBaseCurrency')}
                name="merchentDeliveryFeeInBaseCurrency"
                value={formData.merchentDeliveryFeeInBaseCurrency}
                onChange={(e) => {
                  handleInputChangeWithValidation(e);
                  // Call conversion on change
                  if (e.target.value && parseFloat(e.target.value) > 0 && handleConvertMerchantDeliveryFee) {
                    handleConvertMerchantDeliveryFee(parseFloat(e.target.value));
                  }
                }}
                onBlur={() => handleFieldBlur('merchentDeliveryFeeInBaseCurrency')}
                placeholder={t('placeholders.enterMerchantDeliveryFee', 'Enter merchant delivery fee')}
                required
              />
              {renderErrorMessage('merchentDeliveryFeeInBaseCurrency')}
            </div>
            <div className="col-md-3">
              <label className="form-label">
                {t('fields.merchantDeliveryFee')} (USD)
              </label>
              <input
                type="number"
                disabled
                className={getFieldClassName('merchentDeliveryFee')}
                name="merchentDeliveryFee"
                value={formData.merchentDeliveryFee}
                onChange={handleInputChangeWithValidation}
                onBlur={() => handleFieldBlur('merchentDeliveryFee')}
                placeholder="Auto-calculated"
                readOnly
              />
              {renderErrorMessage('merchentDeliveryFee')}
            </div>
          </>
        )}

      </div>

      {/* Dimensions */}
      <div className="row mt-3">
        <div className="col-md-3">
          <label className="form-label">
            {t('fields.lengthMeasurementUnit')}
          </label>
          <select
            className="form-control"
            name="lengthMeasurementUnitId"
            value={formData.lengthMeasurementUnitId || ''}
            onChange={handleInputChange}
          >
            <option value="">{t('placeholders.selectLengthUnit')}</option>
            {lengthMeasurementUnits.map((unit: any) => (
              <option key={unit.id} value={unit.id}>
                {unit.name} ({unit.symbol})
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">
            {t('fields.unitWidth')} <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={getFieldClassName('width')}
            name="width"
            value={formData.width}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('width')}
            placeholder={t('placeholders.enterWidth')}
            required
          />
          {renderErrorMessage('width')}
        </div>
        <div className="col-md-3">
          <label className="form-label">
            {t('fields.unitHeight')} <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={getFieldClassName('height')}
            name="height"
            value={formData.height}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('height')}
            placeholder={t('placeholders.enterHeight')}
            required
          />
          {renderErrorMessage('height')}
        </div>
        <div className="col-md-3">
          <label className="form-label">
            {t('fields.unitLength')} <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={getFieldClassName('length')}
            name="length"
            value={formData.length}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('length')}
            placeholder={t('placeholders.enterLength')}
            required
          />
          {renderErrorMessage('length')}
        </div>

      </div>

      {/* Measurement Units */}
      <div className="row mt-3">

        <div className="col-md-3">
          <label className="form-label">
            {t('fields.weightMeasurementUnit')}
          </label>
          <select
            className="form-control"
            name="weightMeasurementUnitId"
            value={formData.weightMeasurementUnitId || ''}
            onChange={handleInputChange}
          >
            <option value="">{t('placeholders.selectWeightUnit')}</option>
            {weightMeasurementUnits.map((unit: any) => (
              <option key={unit.id} value={unit.id}>
                {unit.name} ({unit.symbol})
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3">
          <label className="form-label">
            {t('fields.unitWeight')} <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            className={getFieldClassName('weight')}
            name="weight"
            value={formData.weight}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('weight')}
            placeholder={t('placeholders.enterWeight')}
            required
          />
          {renderErrorMessage('weight')}
        </div>
      </div>

      <div className="row mt-3">
        <div className="col-md-4">
          <label className="form-label">
            {t('fields.shippingCompany')} <span className="text-danger">*</span>
          </label>
          <select
            className={getFieldClassName('shippingCompanyId')}
            name="shippingCompanyId"
            value={formData.shippingCompanyId || ""}
            onChange={handleInputChangeWithValidation}
            onBlur={() => handleFieldBlur('shippingCompanyId')}
            required
          >
            <option value="">{t('placeholders.selectShippingCompany')}</option>
            {shippingCompanies.map((item: any) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          {renderErrorMessage('shippingCompanyId')}
        </div>
        <div className="col-md-4">
          <label className="form-label">{t('fields.itemType')}</label>
          <select
            key={formData.itemType}
            className="form-control"
            name="itemType"
            value={formData.itemType}
            onChange={handleInputChange}
          >
            <option value={0} disabled={selectedShippingCompany && !selectedShippingCompany.hasNormal}>
              {t('itemTypes.0')}
            </option>
            <option value={1} disabled={selectedShippingCompany && !selectedShippingCompany.hasDangerous}>
              {t('itemTypes.1')}
            </option>
          </select>
        </div>
        <div className="col-md-4">
          <label className="form-label">{t('fields.deliveryMode')}</label>
          <select
            key={formData.deliveryMode}
            className="form-control"
            name="deliveryMode"
            value={formData.deliveryMode}
            onChange={handleInputChange}
          >
            <option value={0} disabled={selectedShippingCompany && !selectedShippingCompany.hasEconomy}>
              {t('deliveryModes.0')}
            </option>
            <option value={1} disabled={selectedShippingCompany && !selectedShippingCompany.hasFast}>
              {t('deliveryModes.1')}
            </option>
          </select>
        </div>

      </div>

      <div className="row mt-3">
        <div className="col-md-4">
          <div className="form-check form-switch d-flex align-items-center gap-2 mb-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="calculatorLbTransportPriceSwitch"
              checked={isLBPriceEnabled}
              onChange={() => setIsLBPriceEnabled(!isLBPriceEnabled)}
              style={{ margin: 0 }}
            />
            <label className="form-check-label mb-0" htmlFor="calculatorLbTransportPriceSwitch">
              {t('fields.lbTransportPrice')}
            </label>
          </div>

          <input
            type="number"
            className="form-control"
            name="shippingCompanyPricePerLB"
            value={formData.shippingCompanyPricePerLB}
            onChange={handleInputChange}
            disabled={!isLBPriceEnabled}
            placeholder={t('placeholders.enterLbPrice')}
          />
        </div>
        <div className="col-md-4">
          <div className="form-check form-switch d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="shippingInsuranceSwitch"
              name="hasShippingInssurance"
              checked={formData.hasShippingInssurance}
              onChange={handleInputChange}
              style={{ margin: 0 }}
            />
            <label className="form-check-label mb-0" htmlFor="shippingInsuranceSwitch">
              {t('fields.hasShippingInsurance')}?
            </label>
          </div>
        </div>
        {/* <div className="col-md-3">
          <label className="form-label">{t('fields.dgFees')}</label>
          <input
            type="number"
            className="form-control"
            name="dgFees"
            value={formData.dgFees || ""}
            onChange={handleInputChange}
            placeholder={t('placeholders.enterDgFees')}
            disabled
          />
        </div> */}
        <div className="col-md-4">
          <div className="form-check form-switch d-flex align-items-center gap-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="enableDGFeesSwitch"
              name="enableDGFees"
              checked={formData.enableDGFees}
              onChange={handleInputChange}
              style={{ margin: 0 }}
            />
            <label className="form-check-label mb-0" htmlFor="enableDGFeesSwitch">
              {t('fields.enableDGFees')}
            </label>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="text-center mt-4">
        <button
          className={`btn btn-primary ${isRTL ? 'ms-2' : 'me-2'}`}
          type="button"
          onClick={handleCalculateOrder}
          disabled={calculating || resetting}
        >
          {calculating ? (
            <span className={`spinner-border spinner-border-sm ${isRTL ? 'ms-1' : 'me-1'}`} role="status" aria-hidden="true"></span>
          ) : (
            <i className="fa fa-dollar"></i>
          )}
          {calculating ? t('actions.calculating') : t('actions.calculateOrder')}
        </button>
        <button
          className={`btn btn-primary ${isRTL ? 'ms-2' : 'me-2'}`}
          type="button"
          onClick={resetForm}
          disabled={calculating || resetting}
        >
          {resetting ? (
            <span className={`spinner-border spinner-border-sm ${isRTL ? 'ms-1' : 'me-1'}`} role="status" aria-hidden="true"></span>
          ) : (
            <i className="fa fa-recycle"></i>
          )}
          {t('actions.reset')}
        </button>
      </div>

      {/* Calculation Results */}
      {result && (
        <div className="row mt-5">
          <div className="col-12">
            <h4 className="text-primary mb-3">{t('common:calculator.result')}</h4>
            {type === 'order' ? (
              <div className="row g-3">
                {/* Order Price */}
                <div className="col-md-3">
                  <div className="card bg-gradient-info text-black h-100">
                    <div className="card-body text-center py-4">
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <h6 className="mb-2 text-black-50">{t('common:calculator.orderPrice')}</h6>
                        <h3 className="fw-bold mb-0 text-black">
                          ${formatAmount(result.orderPrice)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Merchant Delivery Fee */}
                <div className="col-md-3">
                  <div className="card bg-gradient-secondary text-black h-100">
                    <div className="card-body text-center py-4">
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <h6 className="mb-2 text-black-50">{t('orders:fields.merchantDeliveryFee')}</h6>
                        <h3 className="fw-bold mb-0 text-black">
                          ${formatAmount(result.merchentDeliveryFee)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Service Price */}
                <div className="col-md-3">
                  <div className="card bg-gradient-warning text-black h-100">
                    <div className="card-body text-center py-4">
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <h6 className="mb-2 text-black-50">{t('common:calculator.servicePrice')}</h6>
                        <h3 className="fw-bold mb-0 text-black">
                          ${formatAmount(result.servicePrice)}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Total Price */}
                <div className="col-md-3">
                  <div className="card bg-gradient-success text-black h-100">
                    <div className="card-body text-center py-4">
                      <div className="d-flex flex-column align-items-center justify-content-center">
                        <h6 className="mb-2 text-black-50">{t('common:calculator.totalPrice')}</h6>
                        <h3 className="fw-bold mb-0 text-black">
                          ${formatAmount(result.totalPrice)} ({formatAmount(result.totalPriceIqd)} {iqdLabel})
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="card bg-gradient-success text-black">
                <div className="card-body text-center py-4">
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <h5 className="mb-2 text-black-50">{t('common:calculator.totalPrice')}</h5>
                    <h1 className="display-4 fw-bold mb-0 text-black">
                      ${formatAmount(result.totalPrice)} ({formatAmount(result.totalPriceIqd)}{iqdLabel})
                    </h1>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
